import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloError } from '@apollo/client';

import {
  useGroupJoinLinkInfoQuery,
  GroupJoinLinkInfoQuery as GroupJoinLinkInfoDoc,
  useSaveGroupLessonLinkMutation,
  usePutGroupLinkOverrideMutation,
  useDropGroupLinkOverrideMutation,
  useLessonJoinClicksListLazyQuery,
  LessonJoinClicksListQuery as LessonJoinClicksListDoc,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';
import { useStableRefetch } from './useStableRefetch';

/* ---------- spec 0013 — group lesson join link ---------- */

export type IGroupJoinLinkInfo = NonNullable<GroupJoinLinkInfoDoc['group']>;
export type IGroupLinkOverride = NonNullable<
  IGroupJoinLinkInfo['activeLinkOverride']
>;

interface IGroupJoinLinkHook {
  /** The permanent lesson link ('' when not set). */
  lessonJoinUrl: string;
  /** Today's temporary override, if any. */
  override?: IGroupLinkOverride;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
  /** `setGroupLessonLink` — empty string clears the permanent link. */
  saveMainLink: (joinUrl: string) => Promise<boolean>;
  /** `setGroupLessonLinkOverride` — `date` is `YYYY-MM-DD`. */
  setOverride: (joinUrl: string, date: string) => Promise<boolean>;
  /** `clearGroupLessonLinkOverride` for the given `YYYY-MM-DD` date. */
  clearOverride: (date: string) => Promise<boolean>;
  /** True while any of the three mutations is in flight. */
  mutating: boolean;
}

/**
 * Data + actions for the `GroupJoinLinkCard` (spec 0013). The query selects
 * `lessonJoinUrl` / `activeLinkOverride`, whose backend resolvers throw
 * `PermissionDenied` outside Admin / AccountManager / Methodist — mount the
 * consuming component behind `visibleFor(roles, ZOOM_ROLES)` only.
 *
 * Mutation failures arrive as top-level GraphQL errors (not `customErrors`),
 * so every action catches and toasts, then reports success as a boolean.
 */
export const useGroupJoinLink = (groupId?: string): IGroupJoinLinkHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const { data, loading, error, refetch } = useGroupJoinLinkInfoQuery({
    variables: { groupId: groupId || '' },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const stableRefetch = useStableRefetch(refetch);

  const [runSave, { loading: saving }] = useSaveGroupLessonLinkMutation();
  const [runPut, { loading: putting }] = usePutGroupLinkOverrideMutation();
  const [runDrop, { loading: dropping }] = useDropGroupLinkOverrideMutation();

  const toastError = useCallback(
    (e: unknown) => {
      addToast({
        type: GlobalToastTypeNames.GroupEdit,
        data: {
          type: ToastTypes.error,
          title: t('pages.groupDetail.joinLink.toastError'),
          text: (e as Error).message,
        },
      });
    },
    [addToast, t],
  );

  const toastOk = useCallback(
    (title: string) => {
      addToast({
        type: GlobalToastTypeNames.GroupEdit,
        data: { type: ToastTypes.success, title },
      });
    },
    [addToast],
  );

  const saveMainLink = useCallback(
    async (joinUrl: string): Promise<boolean> => {
      if (!groupId) return false;
      try {
        await runSave({
          variables: { groupId: Number(groupId), joinUrl: joinUrl.trim() },
        });
        toastOk(t('pages.groupDetail.joinLink.toastSaved'));
        stableRefetch();
        return true;
      } catch (e) {
        toastError(e);
        return false;
      }
    },
    [groupId, runSave, toastOk, toastError, stableRefetch, t],
  );

  const setOverride = useCallback(
    async (joinUrl: string, date: string): Promise<boolean> => {
      if (!groupId) return false;
      try {
        await runPut({
          variables: {
            groupId: Number(groupId),
            joinUrl: joinUrl.trim(),
            date,
          },
        });
        toastOk(t('pages.groupDetail.joinLink.toastOverrideSet'));
        stableRefetch();
        return true;
      } catch (e) {
        toastError(e);
        return false;
      }
    },
    [groupId, runPut, toastOk, toastError, stableRefetch, t],
  );

  const clearOverride = useCallback(
    async (date: string): Promise<boolean> => {
      if (!groupId) return false;
      try {
        await runDrop({ variables: { groupId: Number(groupId), date } });
        toastOk(t('pages.groupDetail.joinLink.toastOverrideCleared'));
        stableRefetch();
        return true;
      } catch (e) {
        toastError(e);
        return false;
      }
    },
    [groupId, runDrop, toastOk, toastError, stableRefetch, t],
  );

  return {
    lessonJoinUrl: data?.group?.lessonJoinUrl || '',
    override: data?.group?.activeLinkOverride ?? undefined,
    loading,
    error,
    refetch: stableRefetch,
    saveMainLink,
    setOverride,
    clearOverride,
    mutating: saving || putting || dropping,
  };
};

/* ---------- spec 0013 — per-lesson click attendance (lazy) ---------- */

export type ILessonJoinClick = NonNullable<
  NonNullable<LessonJoinClicksListDoc['lessonJoinClicks']>[number]
>;

interface ILessonJoinClicksHook {
  /** Fire the lazy query for one lesson (id as it comes off the row). */
  load: (lessonId: string) => void;
  clicks: ILessonJoinClick[];
  loading: boolean;
  called: boolean;
  error?: ApolloError;
}

/**
 * Lazy click list for the «Кто заходил» popover on a `LessonsTable` row.
 * One hook instance per row — the popover fetches on first open only.
 */
export const useLessonJoinClicks = (): ILessonJoinClicksHook => {
  const [
    run,
    { data, loading, called, error },
  ] = useLessonJoinClicksListLazyQuery({ fetchPolicy: 'cache-and-network' });

  const load = useCallback(
    (lessonId: string) => {
      run({ variables: { lessonId: Number(lessonId) } });
    },
    [run],
  );

  return {
    load,
    clicks: (data?.lessonJoinClicks || []).filter((c): c is ILessonJoinClick =>
      Boolean(c),
    ),
    loading,
    called,
    error,
  };
};
